import React, { useEffect, useMemo, useRef, useState } from 'react';

// Generic searchable dropdown (combobox-like) used by ModelSelect and IngestionGroupSelect
// Props:
// - items: Array<{ id: string, name: string }>
// - value: string
// - onChange: function(id)
// - placeholder: string
// - disabled: boolean
// - allowNone: boolean (default false)
// - noneLabel: string (default 'None')
const GenericDropdown = ({
  items = [],
  value = '',
  onChange,
  placeholder = 'Select...',
  disabled = false,
  allowNone = false,
  noneLabel = 'None',
  getItemSearchText,
  renderOption,
  renderValue,
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [openUp, setOpenUp] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const containerRef = useRef(null);

  const options = useMemo(() => {
    const base = Array.isArray(items) ? items : [];
    const withNone = allowNone ? [{ id: '', name: noneLabel }, ...base] : base;
    const q = (query || '').trim().toLowerCase();
    if (!q) return withNone;
    return withNone.filter((m) => {
      const hay = typeof getItemSearchText === 'function'
        ? String(getItemSearchText(m) || '')
        : String(m.name || m.id || '');
      return hay.toLowerCase().includes(q);
    });
  }, [items, query, allowNone, noneLabel, getItemSearchText]);

  const selectedName = useMemo(() => {
    if (!value && allowNone) return noneLabel;
    const found = (items || []).find(m => m.id === value);
    if (!found) return '';
    if (typeof renderValue === 'function') return renderValue(found);
    return found.name || found.id;
  }, [items, value, allowNone, noneLabel, renderValue]);

  useEffect(() => {
    const onDocClick = (e) => {
      const inputEl = inputRef.current;
      const listEl = listRef.current;
      if (!inputEl) return;
      const target = e.target;
      if (inputEl.contains(target)) return;
      if (!listEl) return;
      if (!listEl.contains(target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const commit = (id) => {
    if (disabled) return;
    onChange && onChange(id);
    setOpen(false);
    setQuery('');
  };

  // Decide dropdown direction based on midpoint of parent container
  const decideDirection = () => {
    try {
      const inputEl = inputRef.current;
      if (!inputEl) return setOpenUp(false);
      const container = containerRef.current?.parentElement || containerRef.current;
      const inputRect = inputEl.getBoundingClientRect();
      const parentRect = (container && container.getBoundingClientRect && container.getBoundingClientRect()) || null;
      if (!parentRect) { setOpenUp(false); return; }
      const triggerCenterY = inputRect.top + inputRect.height / 2;
      const parentMidY = parentRect.top + parentRect.height / 2;
      setOpenUp(triggerCenterY > parentMidY);
    } catch (_e) {
      setOpenUp(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    decideDirection();
    const onResize = () => decideDirection();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      setTimeout(decideDirection, 0);
      setHighlight(0);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, Math.max(options.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[highlight];
      if (opt) commit(opt.id);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`generic-dropdown ${disabled ? 'disabled' : ''}`}>
      <div className="input-wrapper">
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={open ? query : (selectedName || '')}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => !disabled && setOpen(true)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          aria-autocomplete="list"
        />
        <button
          type="button"
          className="dropdown-toggle"
          onClick={() => !disabled && setOpen(o => !o)}
          aria-label="Toggle"
          disabled={disabled}
        >
          <i className="fas fa-chevron-down"></i>
        </button>
      </div>
      {open && (
        <div className={`dropdown-list ${openUp ? 'up' : ''}`} ref={listRef}>
          {options.length === 0 ? (
            <div className="empty">No results</div>
          ) : options.map((opt, idx) => (
            <div
              key={opt.id || `none-${idx}`}
              className={`item ${idx === highlight ? 'highlight' : ''}`}
              onMouseEnter={() => setHighlight(idx)}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(opt.id);
              }}
            >
              {typeof renderOption === 'function' ? renderOption(opt) : (opt.name || opt.id || noneLabel)}
            </div>
          ))}
        </div>
      )}
      <style>{`
        .generic-dropdown { position: relative; }
        .generic-dropdown .input-wrapper { display: flex; align-items: center; gap: 6px; }
        .generic-dropdown input {
          width: 260px;
          padding: 8px 10px;
          background: #fff;
          color: #111;
          border: 1px solid #ccc;
          border-radius: 6px;
        }
        .generic-dropdown .dropdown-toggle {
          background: transparent;
          color: #333;
          border: 1px solid #ccc;
          padding: 6px 8px;
          border-radius: 6px;
          cursor: pointer;
        }
        .generic-dropdown .dropdown-list {
          position: absolute;
          z-index: 30;
          top: 100%;
          left: 0;
          width: 100%;
          max-height: 240px;
          overflow: auto;
          background: #fff;
          color: #111;
          border: 1px solid #ddd;
          border-radius: 8px;
          margin-top: 4px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.08);
        }
        .generic-dropdown .dropdown-list.up {
          top: auto;
          bottom: 100%;
          margin-top: 0;
          margin-bottom: 4px;
          box-shadow: 0 -4px 16px rgba(0,0,0,0.08);
        }
        .generic-dropdown .item { padding: 8px 10px; cursor: pointer; }
        .generic-dropdown .item.highlight { background: #f0f4ff; }
        .generic-dropdown .empty { padding: 10px; color: #666; }
        .generic-dropdown.disabled { opacity: 0.6; pointer-events: none; }

        /* Dark mode overrides */
        .dark-mode .generic-dropdown input {
          background: #111;
          color: #eee;
          border-color: #333;
        }
        .dark-mode .generic-dropdown .dropdown-toggle {
          color: #ddd;
          border-color: #444;
        }
        .dark-mode .generic-dropdown .dropdown-list {
          background: #1f1f1f;
          color: #eee;
          border-color: #333;
          box-shadow: 0 4px 12px rgba(0,0,0,0.4);
        }
        .dark-mode .generic-dropdown .dropdown-list.up {
          box-shadow: 0 -4px 12px rgba(0,0,0,0.4);
        }
        .dark-mode .generic-dropdown .item.highlight { background: #2a2a2a; }
        .dark-mode .generic-dropdown .empty { color: #aaa; }
      `}</style>
    </div>
  );
};

export default GenericDropdown;
